import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { DropdownService } from './dropdown';
import type {
  SaveMemberRequest,
  VerifyOtpRequest,
  ResendOtpRequest,
  ApiEnvelope,
  MemberSaveData,
  VerifyOtpData,
  DropdownItem,
  PremiumRule,
} from '../pages/member/member.model';

/** Coerce one premium-rule row (PascalCase from the API) into {@link PremiumRule}. */
function normalizePremiumRule(r: any): PremiumRule {
  return {
    premiumRuleId: r.premiumRuleId ?? r.PremiumRuleId,
    masterPolicyName: r.masterPolicyName ?? r.MasterPolicyName ?? '',
    coverageType: r.coverageType ?? r.CoverageType ?? '',
    premiumCode: r.premiumCode ?? r.PremiumCode ?? '',
    ageFrom: Number(r.ageFrom ?? r.AgeFrom ?? 0),
    ageTo: Number(r.ageTo ?? r.AgeTo ?? 0),
    basePremium: Number(r.basePremium ?? r.BasePremium ?? 0),
    gstPercentage: Number(r.gstPercentage ?? r.GstPercentage ?? 0),
    doctorConsultant: Number(r.doctorConsultant ?? r.DoctorConsultant ?? 0),
  };
}

/**
 * MemberService — data layer for the member onboarding wizard.
 *
 * Talks to the /Member controller (save, OTP verify/resend, fetch by id) and
 * exposes the two dropdown lookups the wizard's selects need. Every method
 * returns the raw HttpClient Observable; callers subscribe and branch on the
 * `success` flag of the standard {@link ApiEnvelope}. JWT is added by the global
 * interceptor, so no auth handling here.
 */
@Injectable({ providedIn: 'root' })
export class MemberService {
  private http = inject(HttpClient);
  private dropdowns = inject(DropdownService);
  private readonly baseUrl = `${environment.apiUrl}/Member`;

  /** Create a member; backend issues an OTP and returns the {@link MemberSaveData}. */
  save(body: SaveMemberRequest) {
    return this.http.post<ApiEnvelope<MemberSaveData>>(`${this.baseUrl}/Save`, body);
  }

  /** Confirm the member's OTP. Body matches {@link VerifyOtpRequest}. */
  verifyOtp(memberId: number, code: string) {
    const body: VerifyOtpRequest = { memberId, code };
    return this.http.post<ApiEnvelope<VerifyOtpData>>(`${this.baseUrl}/VerifyOtp`, body);
  }

  /** Re-send the OTP for a member. Body matches {@link ResendOtpRequest}. */
  resendOtp(memberId: number) {
    const body: ResendOtpRequest = { memberId };
    return this.http.post<ApiEnvelope>(`${this.baseUrl}/ResendOtp`, body);
  }

  /**
   * Set or correct a member's e-mail after onboarding.
   *
   * A member with no e-mail cannot be charged through Generali, which takes it as a required
   * form field — so the payment screens offer this rather than making someone re-onboard.
   */
  updateEmail(memberId: number, email: string) {
    return this.http.post<ApiEnvelope>(`${this.baseUrl}/UpdateEmail`, { memberId, email });
  }

  /** Fetch a single member by id (id is passed as a query param). */
  getById(id: number) {
    return this.http.get<ApiEnvelope>(`${this.baseUrl}/GetById`, { params: { id } });
  }

  /** Premium rules available to the current user (admin: all, partner: enabled). */
  premiumRules(): Observable<PremiumRule[]> {
    return this.http
      .get<ApiEnvelope<any[]>>(`${environment.apiUrl}/PremiumRule/GetForCurrentUser`)
      .pipe(map((res) => (res?.data ?? []).map(normalizePremiumRule)));
  }

  /** Relationship master for the nominee rows' relationship <select>. */
  relationships(): Observable<DropdownItem[]> {
    return this.dropdowns.globalType('Relationship');
  }
}
